'use client'

import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  Info,
  RotateCcw,
} from 'lucide-react'
import {
  PERM_STAGE_OPTIONS,
  calculateMaxOut,
  formatLongDate,
  formatRemaining,
  type PermStage,
  type RiskLevel,
} from './calculate'
import { trackCtaClicked } from '../../lib/analytics'

const INPUT_CLASS =
  'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white'

const RISK_STYLES: Record<
  RiskLevel,
  { badge: string; bar: string; panel: string }
> = {
  low: {
    badge: 'bg-brand-success/20 text-brand-success-muted',
    bar: 'bg-brand-success-muted',
    panel: 'border-brand-success/40 bg-brand-success/10',
  },
  medium: {
    badge: 'bg-amber-100 text-amber-800',
    bar: 'bg-amber-500',
    panel: 'border-amber-200 bg-amber-50',
  },
  high: {
    badge: 'bg-orange-100 text-orange-800',
    bar: 'bg-orange-500',
    panel: 'border-orange-200 bg-orange-50',
  },
  critical: {
    badge: 'bg-red-100 text-red-800',
    bar: 'bg-red-500',
    panel: 'border-red-200 bg-red-50',
  },
}

function riskBarWidth(monthsRemaining: number): string {
  if (monthsRemaining <= 0) return '100%'
  if (monthsRemaining >= 36) return '12%'
  const pct = 100 - (monthsRemaining / 36) * 88
  return `${Math.min(100, Math.max(12, pct))}%`
}

export function Calculator() {
  const [startDate, setStartDate] = useState('')
  const [previousHlDays, setPreviousHlDays] = useState('0')
  const [recaptureDays, setRecaptureDays] = useState('0')
  const [permStage, setPermStage] = useState<PermStage>('not_started')

  const result = useMemo(() => {
    if (!startDate) return null
    return calculateMaxOut({
      startDate,
      previousHlDays: Number(previousHlDays) || 0,
      recaptureDays: Number(recaptureDays) || 0,
      permStage,
    })
  }, [startDate, previousHlDays, recaptureDays, permStage])

  const clearForm = () => {
    setStartDate('')
    setPreviousHlDays('0')
    setRecaptureDays('0')
    setPermStage('not_started')
  }

  return (
    <div className="space-y-10">
      <div className="space-y-5">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1.5">
            H-1B period start date
          </label>
          <p className="text-sm text-gray-500 mb-2">
            First day of the H-1B period you are counting from — usually your first H-1B start or
            entry date.
          </p>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={INPUT_CLASS}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label
              htmlFor="previousHlDays"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Prior H/L days used
            </label>
            <p className="text-sm text-gray-500 mb-2">Days in H or L status before this period.</p>
            <input
              type="number"
              id="previousHlDays"
              name="previousHlDays"
              min={0}
              step={1}
              value={previousHlDays}
              onChange={(e) => setPreviousHlDays(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="recaptureDays" className="block text-sm font-medium text-gray-700 mb-1.5">
              Recapture days
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Full days outside the U.S. during H-1B that you can document.
            </p>
            <input
              type="number"
              id="recaptureDays"
              name="recaptureDays"
              min={0}
              step={1}
              value={recaptureDays}
              onChange={(e) => setRecaptureDays(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
        </div>

        <div>
          <label htmlFor="permStage" className="block text-sm font-medium text-gray-700 mb-1.5">
            Green card stage
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Where you are in the employer-sponsored PERM / I-140 process.
          </p>
          <select
            id="permStage"
            name="permStage"
            value={permStage}
            onChange={(e) => setPermStage(e.target.value as PermStage)}
            className={INPUT_CLASS}
          >
            {PERM_STAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={clearForm}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Clear
        </button>
      </div>

      {result ? (
        <div className={`rounded-xl border p-6 sm:p-8 space-y-6 ${RISK_STYLES[result.risk].panel}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${RISK_STYLES[result.risk].badge}`}
            >
              {result.riskLabel}
            </span>
            <span className="text-sm text-gray-600">{formatRemaining(result.daysRemaining)}</span>
          </div>

          <div>
            <div className="h-2 rounded-full bg-white/70 overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-500 ${RISK_STYLES[result.risk].bar}`}
                style={{ width: riskBarWidth(result.monthsRemaining) }}
              />
            </div>
            <p className="text-xs text-gray-500">
              Planning urgency rises as remaining time drops below 24, 12, and 6 months.
            </p>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <dt className="text-sm text-gray-600 mb-1">Estimated max-out date</dt>
              <dd className="text-xl font-medium text-gray-900">
                {formatLongDate(result.maxOutDate)}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">With recapture added</dt>
              <dd className="text-xl font-medium text-gray-900">
                {formatLongDate(result.maxOutWithRecapture)}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">Start PERM by about</dt>
              <dd className="text-lg font-medium text-gray-900">
                {formatLongDate(result.recommendedPermStart)}
              </dd>
              <p className="text-xs text-gray-500 mt-1">~24 months before max-out</p>
            </div>
            <div>
              <dt className="text-sm text-gray-600 mb-1">Begin sponsorship talks by</dt>
              <dd className="text-lg font-medium text-gray-900">
                {formatLongDate(result.recommendedDiscussionStart)}
              </dd>
              <p className="text-xs text-gray-500 mt-1">~30 months before max-out</p>
            </div>
          </dl>

          <p className="text-gray-800 leading-relaxed">{result.guidance}</p>

          {result.extensionNote && (
            <div className="flex gap-3 text-sm text-gray-700 bg-white/60 rounded-lg p-4">
              <Info className="w-5 h-5 text-brand-blue shrink-0 mt-0.5" />
              <p>{result.extensionNote}</p>
            </div>
          )}

          <div className="pt-2">
            <Link
              href="/consultation"
              onClick={() =>
                trackCtaClicked({
                  cta_text: 'Discuss your timeline',
                  cta_location: 'mid_page',
                  destination_url: '/consultation',
                })
              }
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-full hover:bg-brand-blue/90 transition-all text-sm font-medium"
            >
              Discuss your timeline
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white/50 p-8 text-center">
          <CalendarClock className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            Enter your H-1B start date to see an estimated max-out date and planning timeline.
          </p>
        </div>
      )}

      <div className="flex gap-3 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl p-4">
        <AlertTriangle className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
        <p>
          This is a planning estimate, not a final legal day count. Exact max-out timing depends on
          your full H/L history, I-94 records, approval notices, travel documentation, and how
          recapture is requested. Speak with an immigration attorney before relying on these dates.
        </p>
      </div>
    </div>
  )
}
